// src/api/api.js
/* ------------------------------------------------------------------
 * Instancia Axios centralizada para IntegraNube
 *
 *  • Usa la base REACT_APP_API_URL (configurada en .env)
 *  • Siempre envía tenantid (minúsculas) y Authorization: Bearer <JWT>
 *  • Refresca token/tenant dinámicamente en cada solicitud (interceptor)
 *  • Exports:
 *      - api (axios instance)
 *      - fetchData / postData / putData / deleteData
 * ----------------------------------------------------------------- */

import axios from 'axios';

/* ═════════════════════ BASE Y TIMEOUT ═════════════════════ */
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || 'https://api.integranube.cl',
  timeout: 3 * 60 * 1000, // 3 minutos
});

/* ═════════════════════ HEADERS DINÁMICOS ═════════════════════
 * – Se leen del localStorage antes de *cada* request.
 * – El token se guarda bajo 'token'; el tenant bajo 'tenantid'.
 * – El backend espera header 'tenantid' en minúsculas.
 * *********************************************************** */
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  const tenantid = localStorage.getItem('tenantid');

  cfg.headers = cfg.headers || {};

  // Autenticación
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  } else {
    delete cfg.headers.Authorization;
  }

  // Contexto tenant
  if (tenantid) {
    cfg.headers.tenantid = tenantid;
  } else {
    delete cfg.headers.tenantid;
  }

  // Si hay body y no hay Content-Type, lo asignamos
  if (cfg.data && !cfg.headers['Content-Type']) {
    cfg.headers['Content-Type'] = 'application/json';
  }

  return cfg;
});

/* ═════════════════════ HANDLER DE ERRORES GLOBAL ═════════════════════
 * – Intercepta 4xx/5xx para normalizar el mensaje.
 * – Solo 401 implica sesión inválida/expirada.
 * – 403 NO debe cerrar sesión (por ejemplo: PIN inválido, rol insuficiente).
 * – Se conserva el error original de Axios para mantener error.response.
 * ******************************************************************* */
api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;
    const url = error.config?.url || '';

    // Solo cerrar sesión ante 401 real y excluyendo endpoints especiales si hiciera falta
    if (status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Mantener mensaje amigable, pero sin destruir error.response
    error.friendlyMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Error de solicitud';

    return Promise.reject(error);
  }
);

export const fetchData = (endpoint, params = {}) =>
  api.get(endpoint, { params }).then(r => r.data);

export const postData = (endpoint, body = {}) =>
  api.post(endpoint, body).then(r => r.data);

export const putData = (endpoint, body = {}) =>
  api.put(endpoint, body).then(r => r.data);

export const deleteData = (endpoint) =>
  api.delete(endpoint).then(r => r.data);

/* Export por defecto */
export default api;